import { CONNECTIONS, type Connection, LOG_GROUPS, LOG_LEVEL, storage } from '@b2x/shared';
import { initializeStore } from '@seamless/store';

import type { AuthenticationState } from '@b2x/authentication-library';

import { loadTranslations } from '@b2x/shared';
import { handleNotifications } from './lib/handle-notifications';
import { initializeProfileConnection } from './lib/initialize-connection';
import { updateNavigationItem } from './lib/update-navigation-item';
import { updateProfilePicture } from './lib/update-profile-picture';
import { B2X_CUSTOMER_BROADCAST_CHANNEL, PROFILE_PERSISTED_CONNECTION } from './utilities/constants';
import { logger } from './utilities/logger';
import { buildTime, name, version } from './utilities/version';

declare global {
  interface Window {
    GLOBAL_PL_AVAILABLE: boolean;
  }
}

window.GLOBAL_PL_AVAILABLE = true;

async function bootstrap() {
  logger(`Profile library ${version} - ${buildTime}`, {
    args: [],
    group: LOG_GROUPS.BOOTSTRAP,
    level: LOG_LEVEL.INFO,
  });

  const store = initializeStore();

  await new Promise((resolve) => {
    const timeout = setTimeout(async () => {
      logger('Unable to retrieve auth connection on expected time', {
        args: [],
        group: LOG_GROUPS.BOOTSTRAP,
        level: LOG_LEVEL.WARN,
      });

      const emptyAuthState: AuthenticationState = {
        jwe: null,
        claims: null,
        identityProvider: '',
        isPending: false,
      } as any;

      await initializeProfileConnection(emptyAuthState);

      resolve(null);
    }, 30000);

    const subscription = store.subscribe(CONNECTIONS.AUTH_CONNECTION_NAME as Connection, async (authState) => {
      if (!authState.isPending) {
        resolve(await initializeProfileConnection(authState));
        clearTimeout(timeout);
      }
    });

    // tabs sync
    const channel = new BroadcastChannel(B2X_CUSTOMER_BROADCAST_CHANNEL);
    channel.onmessage = async (event) => {
      const { key, value } = event.data;
      if (key !== PROFILE_PERSISTED_CONNECTION) {
        return;
      }
      storage.session.set(PROFILE_PERSISTED_CONNECTION, value);
    };

    const handlePageHide = () => {
      logger('Page is being hidden', {
        args: [],
        group: LOG_GROUPS.BOOTSTRAP,
        level: LOG_LEVEL.LOG,
      });
      subscription.unsubscribe();
      channel.close();
    };

    window.addEventListener('pagehide', handlePageHide);
  });

  await loadTranslations(process.env.PHRASE_PROJECT_ID as string, name);

  handleNotifications();

  await updateNavigationItem();

  await updateProfilePicture();
}

bootstrap();
